import type { HomeyAPI, FlowCardAction } from 'homey-api';

import { __DEV__ } from '../../lib/__dev__.js';
import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type FlowCardActionsProps = {
  [K in keyof FlowCardAction as FlowCardAction[K] extends Function ? never : K]: FlowCardAction[K];
};

type FlowCardActionsData = { [key: string]: FlowCardAction };
type FlowCardActionsById = { [key: string]: FlowCardActionsProps };

interface State {
  data: FlowCardActionsData | null;
  byId: FlowCardActionsById | null;
  isLoading: boolean;
  error: unknown;
}

export type FlowCardActionsStoreState = State;

export class FlowCardActionsStore extends BaseApiStore<State, FlowCardAction> {
  constructor() {
    super('flow-card-actions');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.flow, reference: this });

      if (abortController.signal.aborted) return;

      const flowCardActionsPromise = api.flow.getFlowCardActions({ $cache: cache });

      const [flowCardActionsData] = await Promise.all([flowCardActionsPromise]);

      if (abortController.signal.aborted) return;

      const flowCardActions = Object.values(flowCardActionsData);

      const data: FlowCardActionsData = {};
      const byId: FlowCardActionsById = {};

      for (const flowCardActionInstance of flowCardActions) {
        const flowCardActionReference = { ...flowCardActionInstance };

        data[flowCardActionReference.id] = flowCardActionInstance;
        byId[flowCardActionReference.id] = flowCardActionReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.flow, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.flow.on('flowcardaction.create', this.handleCreate);
    api.flow.on('flowcardaction.update', this.handleUpdate);
    api.flow.on('flowcardaction.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.flow.off('flowcardaction.create', this.handleCreate);
    api.flow.off('flowcardaction.update', this.handleUpdate);
    api.flow.off('flowcardaction.delete', this.handleDelete);
  }

  getInstanceFromCache(flowCardAction: { id: string }) {
    return this.getCache()[flowCardAction.id];
  }

  getCache(): { [key: string]: FlowCardAction } {
    const api = this.getApi();
    const cache = api.flow.__cache['flowcardaction'];
    return cache;
  }

  handleCreate = (createdFlowCardAction: FlowCardAction) => {
    this.DEBUG && console.info(`create:flowcardaction:${createdFlowCardAction.id}`);
    const state = this.get();
    const flowCardActionInstance = this.getInstanceFromCache(createdFlowCardAction);

    if (flowCardActionInstance == null) return;

    const flowCardActionReference = { ...flowCardActionInstance };

    this.set({
      data: {
        ...state.data,
        [createdFlowCardAction.id]: flowCardActionInstance,
      },
      byId: {
        ...state.byId,
        [createdFlowCardAction.id]: flowCardActionReference,
      },
    });
  };

  handleUpdate = (updatedFlowCardAction: FlowCardAction) => {
    this.DEBUG && console.info(`update:flowcardaction:${updatedFlowCardAction.id}`);

    this.addToQueue(updatedFlowCardAction.id, updatedFlowCardAction);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedFlowCardAction] of Object.entries(updateQueue)) {
        const flowCardActionInstance = this.getInstanceFromCache(queuedFlowCardAction);

        if (state.data == null) {
          this.DEBUG && console.info(`update:flowcardaction:missing-data`);
          continue;
        }

        if (flowCardActionInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:flowcardaction:unknown:${id}`);
          continue;
        }

        const flowCardActionReference = { ...flowCardActionInstance };

        nextById[id] = flowCardActionReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:flowcardaction:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedFlowCardAction: FlowCardAction) => {
    this.DEBUG && console.info(`delete:flowcardaction:${deletedFlowCardAction.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:flowcardaction:missing-data`);
      return;
    }

    const flowCardActionInstance = state.data[deletedFlowCardAction.id];

    if (flowCardActionInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[flowCardActionInstance.id];
    delete nextById[flowCardActionInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const advancedFlows = Object.values(cache ?? {});
    const data: FlowCardActionsData = {};
    const byId: FlowCardActionsById = {};

    for (const flowCardConditionInstance of advancedFlows) {
      const flowCardConditionReference = { ...flowCardConditionInstance };
      data[flowCardConditionReference.id] = flowCardConditionInstance;
      byId[flowCardConditionReference.id] = flowCardConditionReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const flowCardActionsStore = new FlowCardActionsStore();
